import { t } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/ui/components/arandu/button';
import { IconButton } from '@/ui/components/arandu/icon-button';
import { LinkButton } from '@/ui/components/arandu/link-button';
import { useEmblaCarousel, usePrevNextButtons } from '@/ui/components/carousel';
import { Icon, Icons } from '@/ui/components/icon';
import { useSession } from '@/ui/hooks/use-session';

export const Integration = () => {
  const { t } = useTranslation();
  const { session } = useSession();
  const nav = useNavigate();

  return (
    <section className='w-full py-xl flex flex-col gap-xl pl-mini bg-[radial-gradient(circle_at_bottom_left,_#9A97F9_0%,_#E4E3FF_100%)]'>
      <div className='w-full flex flex-col gap-xl text-center mx-auto lg:max-w-[76rem] md:pr-mini'>
        <header className='w-full max-w-[75rem] mx-auto flex flex-col gap-xl pr-mini'>
          <h2 className='ts-heading-lg md:ts-heading-display text-base-primary-900 md:text-base-primary-600 text-center max-w-[60rem] mx-auto'>
            <Trans
              components={{
                span: <span className='text-base-primary-600 md:text-base-primary-500' />,
              }}
              i18nKey='home.presentation.integrate-events'
            />
          </h2>

          <p className='ts-subtitle-sm text-ctx-content-base'>{t('home.integration.description')}</p>
        </header>

        <IntegrationCarrousel />

        <p className='ts-paragraph-sm text-ctx-content-base pr-mini'>
          <Trans
            components={{
              span: <span className='block' />,
            }}
            i18nKey='home.integration.and-more'
          />
        </p>

        <Button
          className='mx-auto !duration-500 ease-in-out'
          onClick={() => nav(`${session ? '/events' : '/auth/login'}`)}
          type='button'
          size='lg'
        >
          {t('start-now')}
        </Button>
      </div>
    </section>
  );
};

const IntegrationCarrousel = () => {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: false,
  });

  const { onPrevButtonClick, onNextButtonClick, prevBtnDisabled, nextBtnDisabled } = usePrevNextButtons([
    emblaRef,
    emblaApi,
  ]);

  return (
    <div className='embla w-full flex flex-col gap-sm'>
      <div className='w-full flex flex-col max-w-[76rem] gap-sm md:flex-row md:justify-between md:items-center mx-auto'>
        <h3 className='text-ctx-content-base ts-subtitle-md text-left'>
          {t('home.integration.resources-organization')}
        </h3>

        <div className='flex gap-mini justify-end pr-mini md:pr-0'>
          <IconButton
            hierarchy='secondary'
            className='embla__prev !duration-500 ease-in-out'
            ariaLabel={t('components.pagination.previous')}
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            icon={() => <Icon className='text-ctx-content-title' icon='arrow-left' />}
          />
          <IconButton
            hierarchy='secondary'
            className='embla__next !duration-500 ease-in-out'
            ariaLabel={t('components.pagination.next')}
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            icon={() => <Icon className='text-ctx-content-title' icon='arrow-right' />}
          />
        </div>
      </div>

      <div className='flex w-full'>
        <div className='embla__viewport overflow-hidden' ref={emblaRef}>
          <div className='embla__container flex gap-xs'>
            {integrations.map((integration) => (
              <div className='group' key={integration.title}>
                <div className='embla__slide min-w-[15.75rem] max-w-[15.75rem] h-[21.125rem] flex flex-col gap-xxs bg-ctx-layout-surface hover:bg-base-primary-800 rounded-lg pt-[5.843rem] px-xxs transition-all duration-500'>
                  <div className='flex flex-col items-start gap-mini text-left transform transition-all duration-700 group-hover:-translate-y-[1.936rem]'>
                    <div className='border-hairline border-solid border-[#D1D1D6] size-sm rounded-md flex items-center justify-center mb-nano'>
                      <Icon
                        className='text-ctx-content-title size-[1.171rem] group-hover:text-base-primary-100'
                        icon={integration.icon}
                      />
                    </div>
                    <h3
                      className={cn('text-ctx-interactive-primary ts-heading-md group-hover:text-base-primary-100', {
                        'mb-xxs group-hover:mb-0': integration.title.length < 16,
                      })}
                    >
                      {integration.title}
                    </h3>
                    {integration.link && (
                      <LinkButton
                        trailingIcon={() => <Icon icon='arrow-up-right' />}
                        className='!text-ctx-content-title group-hover:!text-base-primary-100'
                        hierarchy='secondary'
                      >
                        {t('learn-more')}
                      </LinkButton>
                    )}
                    <p className='ts-paragraph-xs hidden group-hover:block text-base-primary-200'>
                      <Trans
                        components={{
                          strong: <strong />,
                        }}
                      >
                        {integration.description}
                      </Trans>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const integrations: {
  icon: Icons;
  title: string;
  description: string;
  link?: string;
}[] = [
  {
    icon: 'bar-chart-big',
    title: t('smart-marketing'),
    description: t('home.integration.segmented-campaings'),
  },
  {
    icon: 'puzzle',
    title: t('dashboard-title'),
    description: t('home.integration.track-progress'),
  },
  {
    icon: 'cpu',
    title: t('advanced-analytics'),
    description: t('home.integration.audience-metrics'),
  },
  {
    icon: 'users-icon',
    title: t('shared-access'),
    description: t('home.integration.multi-organizer-access'),
  },
  {
    icon: 'lock',
    title: t('cloakroom-and-belongings'),
    description: t('home.integration.personal-items-management'),
  },
  {
    icon: 'upload',
    title: t('import-external-lists'),
    description: t('home.integration.bulk-upload-participants'),
  },
  {
    icon: 'tag',
    title: t('discount-coupons'),
    description: t('home.integration.promotional-codes'),
  },
];
